import NumberInput from "./numberInput.element";
import {CSSProperties, ReactNode} from "react";
import {Tooltip} from "@mui/material";
import {FieldConstraint} from "../models/attributeManifest.model";
import {useFieldDoc} from "./useFieldDoc";

interface NumberInputLabelProps {
    // Optional: when omitted, the label falls back to the manifest field's Label
    // (from [FieldDoc]) so a field's presentation can live entirely in the Client
    // model. An explicit title always wins.
    title?: string;
    value: number;
    step?: string;
    displayDecimals?: number;
    tooltip?: string;
    style?: CSSProperties;
    inputStyle?: CSSProperties;
    // Constraint source. Either resolved by manifest data-path key (`field`, e.g.
    // "unit.properties.attack") or passed directly (`constraint`, e.g. a metadata
    // param's Min/Max). The input clamps to it on commit. Both optional — omit for
    // an unconstrained field.
    field?: string;
    constraint?: FieldConstraint | null;
    // A button joined to the input's right edge (the ↺ of DefaultedNumberInputLabel). The
    // two together take a plain input's width, so the field lines up with its neighbours.
    accessory?: ReactNode;
    onChange(value: number): void;
}

const JOINED_INPUT_STYLE: CSSProperties = {
    boxSizing: 'border-box', width: 0, minWidth: 0, flexGrow: 1,
    borderTopRightRadius: 0, borderBottomRightRadius: 0, borderRight: 'none',
};

function NumberInputLabel(props: NumberInputLabelProps) {
    const {constraint, label, tip} = useFieldDoc({
        field: props.field, title: props.title, tooltip: props.tooltip, constraint: props.constraint,
    });

    const input = (style?: CSSProperties) =>
        <NumberInput value={props.value} step={props.step} displayDecimals={props.displayDecimals} style={style} constraint={constraint} onChange={props.onChange}/>;

    return (
        <div className={'layout horizontal stretch gap flex-grow'}
             style={{...{alignItems: 'center', gap: 10}, ...props.style}}>
            <div className={'layout horizontal center gap'}>
                {tip
                    ? <Tooltip title={tip} arrow disableInteractive>
                        <p className={'flex-grow'} style={{cursor: 'help'}}>{label}</p>
                    </Tooltip>
                    : <p className={'flex-grow'}>{label}</p>}
            </div>
            {props.accessory
                // An input's width is the browser's intrinsic one, so an invisible plain
                // input in the same grid cell sets it, and the input and accessory share it.
                ? <div style={{display: 'grid'}}>
                    <input type={'number'} aria-hidden tabIndex={-1} readOnly style={{gridArea: '1 / 1', visibility: 'hidden'}}/>
                    <div style={{gridArea: '1 / 1', display: 'flex', minWidth: 0}}>
                        {input({...JOINED_INPUT_STYLE, ...props.inputStyle})}
                        {props.accessory}
                    </div>
                </div>
                : input(props.inputStyle)}
        </div>
    )
}

export default NumberInputLabel;
